import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Fuse from 'fuse.js';
import { documentApi } from '../services/documentApi';

// KST-based time grouping
function getTimeGroup(dateStr) {
  if (!dateStr) return 'older';
  const now = new Date();
  const koreaOffset = 9 * 60 * 60 * 1000;
  const nowKST = new Date(now.getTime() + koreaOffset);
  const dateKST = new Date(new Date(dateStr).getTime() + koreaOffset);

  const todayStart = new Date(nowKST);
  todayStart.setHours(0, 0, 0, 0);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 7);

  if (dateKST >= todayStart) return 'today';
  if (dateKST >= yesterdayStart) return 'yesterday';
  if (dateKST >= weekStart) return 'thisWeek';
  return 'older';
}

const GROUP_LABELS = {
  today: '오늘',
  yesterday: '어제',
  thisWeek: '이번 주',
  older: '이전',
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '방금';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(dateStr).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', month: 'short', day: 'numeric' });
}

const TAB_KEYS = ['new_updates', 'opened', 'updated', 'search'];

const TABS = [
  { key: 'new_updates', label: '새 업데이트', icon: '🔔' },
  { key: 'opened', label: '최근 조회', icon: '👁' },
  { key: 'updated', label: '최근 작업', icon: '✏️' },
  { key: 'search', label: '검색', icon: '🔍' },
];

const POLL_INTERVAL = 30000;

const QuickOpenModal = ({ onClose, onSelect, onProjectDocSelect, initialTab, initialQuery }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [activeTab, setActiveTab] = useState(initialTab || 'new_updates');
  const [documents, setDocuments] = useState({ opened: [], updated: [], new_updates: [] });
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [newUpdatesCount, setNewUpdatesCount] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Fetch documents for tabs (not search)
  const fetchAll = useCallback(() => {
    return Promise.all([
      documentApi.getRecent(100, 'opened'),
      documentApi.getRecent(100, 'updated'),
      documentApi.getRecent(100, 'new_updates'),
    ]).then(([openedRes, updatedRes, newUpdatesRes]) => {
      setDocuments({
        opened: openedRes.data || [],
        updated: updatedRes.data || [],
        new_updates: newUpdatesRes.data || [],
      });
      setNewUpdatesCount((newUpdatesRes.data || []).length);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll for new updates
  useEffect(() => {
    const interval = setInterval(() => {
      documentApi.getRecent(100, 'new_updates')
        .then(res => {
          const data = res.data || [];
          setDocuments(prev => ({ ...prev, new_updates: data }));
          setNewUpdatesCount(data.length);
        })
        .catch(() => {});
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Server-side search with debounce (for search tab)
  useEffect(() => {
    if (activeTab !== 'search') return;
    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      documentApi.search(query.trim(), 50)
        .then(res => {
          setSearchResults(res.data || []);
          setSearchLoading(false);
        })
        .catch(() => {
          setSearchResults([]);
          setSearchLoading(false);
        });
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [query, activeTab]);

  // Current tab's docs (for non-search tabs, apply local Fuse filter)
  const currentDocs = activeTab === 'search' ? searchResults : (documents[activeTab] || []);

  // Fuse.js for local filtering (non-search tabs only)
  const fuse = useMemo(() => {
    if (activeTab === 'search') return null;
    return new Fuse(documents[activeTab] || [], {
      keys: [
        { name: 'title', weight: 0.6 },
        { name: 'tags', weight: 0.3 },
        { name: 'preview', weight: 0.1 },
      ],
      threshold: 0.4,
      includeMatches: true,
      minMatchCharLength: 1,
    });
  }, [documents, activeTab]);

  // Filter results
  const results = useMemo(() => {
    if (activeTab === 'search') return searchResults;

    const tabDocs = documents[activeTab] || [];
    if (!query.trim()) return tabDocs;

    if (query.startsWith('#')) {
      const tagQuery = query.slice(1).toLowerCase();
      if (!tagQuery) return tabDocs;
      return tabDocs.filter(doc => {
        const tags = Array.isArray(doc.tags) ? doc.tags : [];
        return tags.some(t => t.toLowerCase().includes(tagQuery));
      });
    }

    return fuse ? fuse.search(query).map(r => r.item) : tabDocs;
  }, [query, activeTab, documents, searchResults, fuse]);

  // Group results by time
  const groupedResults = useMemo(() => {
    const groups = { today: [], yesterday: [], thisWeek: [], older: [] };
    results.forEach(doc => {
      const effectiveDate = activeTab === 'opened'
        ? (doc.last_opened_at || doc.updated_at)
        : doc.updated_at;
      const group = getTimeGroup(effectiveDate);
      groups[group].push(doc);
    });
    return groups;
  }, [results, activeTab]);

  // Flat list for keyboard navigation
  const flatList = useMemo(() => {
    const list = [];
    ['today', 'yesterday', 'thisWeek', 'older'].forEach(group => {
      groupedResults[group].forEach(doc => list.push(doc));
    });
    return list;
  }, [groupedResults]);

  useEffect(() => { setActiveIndex(0); }, [query, activeTab]);

  const cycleTab = useCallback((dir = 1) => {
    setActiveTab(prev => {
      const idx = TAB_KEYS.indexOf(prev);
      return TAB_KEYS[(idx + dir + TAB_KEYS.length) % TAB_KEYS.length];
    });
  }, []);

  const handleItemSelect = useCallback((doc) => {
    if (doc.doc_source === 'project' && onProjectDocSelect) {
      onProjectDocSelect(doc.project, doc.id);
    } else {
      onSelect(doc.id);
    }
    onClose();
  }, [onSelect, onProjectDocSelect, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatList.length > 0) {
      e.preventDefault();
      const doc = flatList[activeIndex];
      if (doc) handleItemSelect(doc);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      cycleTab(e.shiftKey ? -1 : 1);
    }
  }, [flatList, activeIndex, handleItemSelect, onClose, cycleTab]);

  useEffect(() => {
    if (listRef.current) {
      const active = listRef.current.querySelector('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Auto-switch to search tab when typing starts (if not already on search)
  const handleQueryChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    // If user starts typing on non-search tabs, switch to search
    if (val.trim() && activeTab !== 'search' && !val.startsWith('#')) {
      // Only auto-switch if query is 2+ chars
      if (val.trim().length >= 2) {
        setActiveTab('search');
      }
    }
  }, [activeTab]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const emptyMessage = useMemo(() => {
    if (activeTab === 'search') {
      if (!query.trim()) return '검색어를 입력하세요';
      if (searchLoading) return '검색 중...';
      return '검색 결과가 없습니다';
    }
    if (query) return '검색 결과가 없습니다';
    if (activeTab === 'new_updates') return '확인하지 않은 업데이트가 없습니다 ✅';
    return '최근 문서가 없습니다';
  }, [query, activeTab, searchLoading]);

  const renderItem = (doc, globalIdx) => {
    const isActive = globalIdx === activeIndex;
    const effectiveDate = activeTab === 'opened'
      ? (doc.last_opened_at || doc.updated_at)
      : doc.updated_at;
    const tags = Array.isArray(doc.tags) ? doc.tags : [];
    const isProjectDoc = doc.doc_source === 'project';

    return (
      <div
        key={`${doc.doc_source}-${doc.id}`}
        data-active={isActive}
        className={`qo-item ${isActive ? 'qo-item-active' : ''}`}
        onClick={() => handleItemSelect(doc)}
        onMouseEnter={() => setActiveIndex(globalIdx)}
      >
        <div className="qo-item-main">
          {isProjectDoc && <span className="qo-source-badge">P</span>}
          <span className="qo-item-title">{doc.title || 'Untitled'}</span>
          {doc.is_pinned ? <span className="qo-pin-badge">📌</span> : null}
          {activeTab === 'new_updates' && <span className="qo-update-dot" />}
        </div>
        <div className="qo-item-meta">
          {isProjectDoc && (
            <span className="qo-project-name">{doc.project}</span>
          )}
          {tags.length > 0 && (
            <span className="qo-item-tags">
              {tags.slice(0, 3).map(t => (
                <span key={t} className="qo-tag">#{t}</span>
              ))}
            </span>
          )}
          <span className="qo-item-time">{formatRelativeTime(effectiveDate)}</span>
        </div>
        {doc.preview && (
          <div className="qo-item-preview">
            {doc.preview.replace(/[#*`\-|>]/g, '').trim().slice(0, 80)}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (groupKey, startIdx) => {
    const items = groupedResults[groupKey];
    if (items.length === 0) return { element: null, nextIdx: startIdx };

    return {
      element: (
        <div key={groupKey} className="qo-group">
          <div className="qo-group-label">{GROUP_LABELS[groupKey]}</div>
          {items.map((doc, i) => renderItem(doc, startIdx + i))}
        </div>
      ),
      nextIdx: startIdx + items.length,
    };
  };

  let idx = 0;
  const groupElements = [];
  for (const groupKey of ['today', 'yesterday', 'thisWeek', 'older']) {
    const { element, nextIdx } = renderGroup(groupKey, idx);
    if (element) groupElements.push(element);
    idx = nextIdx;
  }

  const isSearchTabActive = activeTab === 'search';
  const showLoading = loading && !isSearchTabActive;
  const showSearchLoading = isSearchTabActive && searchLoading && query.trim();

  return (
    <div className="qo-overlay" onClick={handleBackdropClick} onKeyDown={handleKeyDown}>
      <div className="qo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qo-search-wrap">
          <svg className="qo-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            className="qo-search-input"
            type="text"
            placeholder={isSearchTabActive ? '전체 문서 검색...' : '노트 검색... (#태그로 필터)'}
            value={query}
            onChange={handleQueryChange}
          />
          <button className="qo-close-btn" onClick={onClose} title="닫기">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18" /><path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="qo-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`qo-tab ${activeTab === tab.key ? 'qo-tab-active' : ''}`}
              onClick={() => { setActiveTab(tab.key); inputRef.current?.focus(); }}
            >
              {tab.label}
              {tab.key === 'new_updates' && newUpdatesCount > 0 && (
                <span className="qo-tab-badge">{newUpdatesCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="qo-results" ref={listRef}>
          {showLoading || showSearchLoading ? (
            <div className="qo-empty">검색 중...</div>
          ) : flatList.length === 0 ? (
            <div className="qo-empty">{emptyMessage}</div>
          ) : (
            groupElements
          )}
        </div>

        <div className="qo-footer">
          <span><kbd>↑↓</kbd> 이동</span>
          <span><kbd>Enter</kbd> 열기</span>
          <span><kbd>Tab</kbd> 탭 전환</span>
          <span><kbd>Esc</kbd> 닫기</span>
          <span className="qo-footer-count">{flatList.length}개 노트</span>
        </div>
      </div>
    </div>
  );
};

export default QuickOpenModal;
